<template>
  <div v-if="isBusiness" class="page-header">
    <h1 class="page-title">
      {{ copy("title") }}
    </h1>
    <p class="page-subtitle">
      {{ copy("subtitle") }}
    </p>
  </div>
  <p v-else class="p-caption gray600">
    {{ copy("subtitle") }}
  </p>

  <div class="whitebox psd2-intro-card psd2-intro-steps">
    <div v-for="step in STEPS" :key="step.key" class="psd2-intro-step">
      <div
        class="psd2-intro-step-marker"
        :class="`psd2-intro-step-marker-${step.marker}`"
      >
        <span v-if="step.marker === 'dot'" class="psd2-intro-step-dot" />
        <font-awesome-icon v-else :icon="['fas', step.icon]" />
      </div>
      <p class="bold psd2-intro-step-title">
        {{ copy(`step.${step.key}.title`) }}
      </p>
      <span class="psd2-intro-step-description">
        {{ copy(`step.${step.key}.description`) }}
      </span>
    </div>
  </div>

  <div class="whitebox psd2-intro-card">
    <h4 class="h bold">
      {{ copy("benefits.title") }}
    </h4>
    <div class="psd2-intro-benefits">
      <div
        v-for="benefit in benefits"
        :key="benefit"
        class="psd2-intro-benefit"
      >
        <font-awesome-icon :icon="['fas', BENEFIT_ICONS[benefit]]" />
        <p>{{ copy(`benefit.${benefit}`) }}</p>
      </div>
    </div>
  </div>

  <button
    id="bnpl_psd2_data_scope_button"
    class="psd2-intro-data-scope"
    @click="showDataScope = true"
  >
    <IconsLock />
    <span class="bold">{{ copy("data_scope.link") }}</span>
    <IconsChevronRight />
  </button>

  <p class="p-small gray600 text-center">
    {{ copy("consent_note") }}
  </p>

  <Popup
    v-if="showDataScope"
    width="600px"
    :title="copy('data_scope.title')"
    :ok-button-text="copy('data_scope.button.ok')"
    @close="showDataScope = false"
  >
    <div class="psd2-intro-data-scope-content">
      <div class="psd2-intro-data-scope-columns">
        <div class="psd2-intro-data-scope-column is-visible">
          <p class="bold">
            {{ copy("data_scope.visible.title") }}
          </p>
          <div
            v-for="item in DATA_SCOPE_ITEMS"
            :key="`visible-${item}`"
            class="psd2-intro-data-scope-item"
          >
            <font-awesome-icon :icon="['fas', 'check']" />
            <p>{{ copy(`data_scope.visible.${item}`) }}</p>
          </div>
        </div>
        <div class="psd2-intro-data-scope-column is-hidden">
          <p class="bold">
            {{ copy("data_scope.hidden.title") }}
          </p>
          <div
            v-for="item in DATA_SCOPE_ITEMS"
            :key="`hidden-${item}`"
            class="psd2-intro-data-scope-item"
          >
            <font-awesome-icon :icon="['fas', 'ban']" />
            <p>{{ copy(`data_scope.hidden.${item}`) }}</p>
          </div>
        </div>
      </div>

      <div class="psd2-intro-data-scope-one-off">
        <font-awesome-icon :icon="['fas', 'lock']" />
        <p class="bold">
          {{ copy("data_scope.one_off") }}
        </p>
      </div>

      <p class="p-small gray600 text-center">
        {{ copy("data_scope.legal") }}
      </p>
    </div>
  </Popup>
</template>

<script setup>
import { useI18n } from "vue-i18n";

/** Fixed for everyone: the marker tells the story, the copy comes from tokens. */
const STEPS = Object.freeze([
  { key: "bank_select", marker: "dot" },
  { key: "bank_login", marker: "icon", icon: "shield-halved" },
  { key: "redirect", marker: "done", icon: "check" },
]);

const BENEFIT_ICONS = Object.freeze({
  no_paper: "file-circle-xmark",
  fast: "clock",
  limit: "money-bill-1",
  tailored: "shield-halved",
});

const DATA_SCOPE_ITEMS = Object.freeze([1, 2, 3]);

const props = defineProps({
  flow: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const isBusiness = props.flow.isBusiness;

const benefits = Object.keys(BENEFIT_ICONS);

const showDataScope = ref(false);

/** The business screens say the same things about a company's account. */
function copy(key) {
  return t(props.flow.token(`psd2.intro.${key}`));
}
</script>

<style scoped lang="scss">
.psd2-intro-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.psd2-intro-steps {
  flex-direction: row;
  gap: 8px;
}

.psd2-intro-step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  position: relative;

  // The dashed run between the markers, drawn from every step but the first.
  &:not(:first-child)::before {
    content: "";
    position: absolute;
    top: 24px;
    right: 50%;
    left: -50%;
    margin: 0 28px;
    border-top: 2px dashed var(--gray200);
  }
}

.psd2-intro-step-marker {
  width: 48px;
  height: 48px;
  border-radius: 999999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid var(--gray200);
  z-index: 1;

  svg {
    width: 20px;
    height: 20px;
  }

  &-done {
    background: var(--brandPrimary);
    border-color: var(--brandPrimary);
    color: white;
  }
}

.psd2-intro-step-dot {
  width: 14px;
  height: 14px;
  border-radius: 999999px;
  background: var(--gray400);
}

.psd2-intro-step-title {
  font-size: 14px;
}

.psd2-intro-step-description {
  font-size: 12px;
  color: var(--gray600);
}

.psd2-intro-benefits {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.psd2-intro-benefit {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: var(--gray600);
  }

  p {
    font-size: 14px;
  }
}

.psd2-intro-data-scope {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 8px;
  background: var(--brandPrimaryLight);
  cursor: pointer;
  text-align: left;

  span {
    flex: 1;
    font-size: 14px;
  }

  svg {
    flex-shrink: 0;
  }
}

.psd2-intro-data-scope-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.psd2-intro-data-scope-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.psd2-intro-data-scope-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;

  &.is-visible {
    background: var(--brandPrimaryLight);

    .psd2-intro-data-scope-item svg {
      color: var(--brandPrimary);
    }
  }

  &.is-hidden {
    background: var(--systemErrorLight);

    .psd2-intro-data-scope-item svg {
      color: var(--systemError);
    }
  }
}

.psd2-intro-data-scope-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
  }

  p {
    font-size: 14px;
  }
}

.psd2-intro-data-scope-one-off {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--gray200);
  border-radius: 8px;

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }

  p {
    font-size: 14px;
  }
}

@media only screen and (max-width: 792px) {
  // The page itself is white on mobile, so the cards are the ones that tint.
  .psd2-intro-card {
    background: var(--gray50);
    box-shadow: none;
    padding: 16px;
  }

  // No room for three descriptions side by side; the titles carry the flow.
  .psd2-intro-step-description {
    display: none;
  }

  .psd2-intro-benefits {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }

  .psd2-intro-data-scope-columns {
    gap: 8px;
  }

  .psd2-intro-data-scope-column {
    padding: 12px;
  }
}
</style>
